import { zodResolver } from '@hookform/resolvers/zod';
import { CONTACT_ROLE_LABELS, CONTACT_ROLES, contactSchema, formatPhone } from '@takumi/shared';
import { useMutation } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Field, Input, Select, Textarea } from '@/components/ui/form';
import { Dialog, DialogContent } from '@/components/ui/primitives';
import { api, errorMessage } from '@/lib/api';
import { useInvalidateCrm } from '@/lib/queries';
import type { Contact } from '@/lib/types';

type In = z.input<typeof contactSchema>;
type Out = z.output<typeof contactSchema>;

export function ContactDialog({ orgId, contact, open, onOpenChange }: { orgId: string; contact: Contact | null; open: boolean; onOpenChange: (o: boolean) => void }) {
  const invalidate = useInvalidateCrm();
  const { register, handleSubmit, reset, formState } = useForm<In, unknown, Out>({ resolver: zodResolver(contactSchema) });
  const e = formState.errors;

  useEffect(() => {
    if (open) {
      reset({
        fullName: contact?.fullName ?? '',
        role: contact?.role ?? 'other',
        position: contact?.position ?? '',
        phone: contact?.phoneE164 ? formatPhone(contact.phoneE164) : (contact?.phone ?? ''),
        whatsapp: contact?.whatsappE164 && contact.whatsappE164 !== contact.phoneE164 ? formatPhone(contact.whatsappE164) : '',
        email: contact?.email ?? '',
        isPrimary: contact?.isPrimary ?? false,
        notes: contact?.notes ?? '',
      });
    }
  }, [open, contact, reset]);

  const save = useMutation({
    mutationFn: (body: Out) => {
      // An empty WhatsApp field means "same as the phone" (defaults server-side for Israeli mobiles).
      const payload = { ...body, whatsapp: body.whatsapp || body.phone };
      return contact ? api.patch(`/organizations/${orgId}/contacts/${contact.id}`, payload) : api.post(`/organizations/${orgId}/contacts`, payload);
    },
    onSuccess: () => {
      invalidate(orgId);
      toast.success(contact ? 'איש הקשר עודכן' : 'איש הקשר נוסף');
      onOpenChange(false);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/organizations/${orgId}/contacts/${contact!.id}`),
    onSuccess: () => {
      invalidate(orgId);
      toast.success('איש הקשר הוסר');
      onOpenChange(false);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title={contact ? 'עריכת איש קשר' : 'איש קשר חדש'}>
        <form onSubmit={handleSubmit((v) => save.mutate(v))} className="grid gap-4 sm:grid-cols-2" noValidate>
          <Field label="שם מלא" required error={e.fullName?.message} className="sm:col-span-2">
            {(id) => <Input id={id} autoFocus {...register('fullName')} />}
          </Field>
          <Field label="תפקיד">
            {(id) => (
              <Select id={id} {...register('role')}>
                {CONTACT_ROLES.map((r) => <option key={r} value={r}>{CONTACT_ROLE_LABELS[r]}</option>)}
              </Select>
            )}
          </Field>
          <Field label="תואר">{(id) => <Input id={id} {...register('position')} placeholder="לדוגמה: מנהלת רכש" />}</Field>
          <Field label="טלפון" error={e.phone?.message}>
            {(id) => <Input id={id} type="tel" dir="ltr" className="text-start" {...register('phone')} />}
          </Field>
          <Field label="WhatsApp" error={e.whatsapp?.message} hint="ריק = אותו מספר כמו הטלפון">
            {(id) => <Input id={id} type="tel" dir="ltr" className="text-start" {...register('whatsapp')} />}
          </Field>
          <Field label="אימייל" error={e.email?.message} className="sm:col-span-2">
            {(id) => <Input id={id} type="email" dir="ltr" className="text-start" {...register('email')} />}
          </Field>
          <Field label="הערות" className="sm:col-span-2">{(id) => <Textarea id={id} rows={2} {...register('notes')} />}</Field>
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input type="checkbox" className="size-4 accent-matcha-700" {...register('isPrimary')} /> איש קשר ראשי
          </label>
          <div className="flex items-center justify-between gap-2 border-t border-line pt-4 sm:col-span-2">
            {contact ? (
              <Button variant="ghost" className="text-danger" onClick={() => confirm('להסיר את איש הקשר?') && remove.mutate()} loading={remove.isPending}>
                <Trash2 /> הסרה
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => onOpenChange(false)}>ביטול</Button>
              <Button type="submit" loading={save.isPending}>שמירה</Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
